/**
 * The form request page's leaf parts (web-account-pages D8): its states, the
 * bootstrap answer, the type line, the terminal screens and icons. Shared by the
 * Mini App's FormRequestPage and the website's phone form page; nothing here
 * reads Telegram or a transport.
 */
import type { ReactNode, Ref } from 'react';
import { FormRenderer } from '@/components/forms/FormRenderer';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';
import styles from './FormRequestPage.module.css';

export type PageState =
  | 'loading'
  | 'form'
  | 'submitting'
  | 'success'
  | 'consumed'
  | 'revoked'
  | 'unavailable'
  | 'error';

export interface BootstrapResponse {
  formId: string;
  formType: 'REGISTRATION' | 'INTAKE' | 'WAIVER' | 'GENERAL';
  title: string;
  description?: string;
  fields: FormDefinition['fields'];
  // form-request-reskin D7: drives the anonymity-reassurance pill.
  isAnonymous?: boolean;
  // web-account-pages D6: the answers kept so far; null when none.
  draft?: { answers: Record<string, unknown>; revision: number } | null;
}

export interface ApiErrorLike {
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
}

/** The scroll content opens with a request-flavoured type line (never the
 *  booking-flow "Required to book" copy — this form isn't gating a booking). */
export function requestTypeLine(formType: string): string {
  switch (formType) {
    case 'REGISTRATION':
      return 'Registration form · Requested by your studio';
    case 'INTAKE':
      return 'Intake form · Requested by your studio';
    case 'WAIVER':
      return 'Waiver · Requested by your studio';
    case 'GENERAL':
    default:
      return 'General form · Requested by your studio';
  }
}

/** Map a bootstrap/submit ApiError to the terminal state it lands on. */
export function terminalStateFor(err: ApiErrorLike): PageState {
  switch (err.code) {
    case 'REQUEST_REVOKED':
      return 'revoked';
    case 'TOKEN_CONSUMED':
      return 'consumed';
    case 'FORM_NOT_AVAILABLE':
    case 'FORM_NOT_FOUND':
    case 'TOKEN_NOT_FOUND':
    case 'INVALID_TOKEN_FORMAT':
      return 'unavailable';
    default:
      break;
  }
  if (err.status === 410) return 'consumed';
  if (err.status === 404) return 'unavailable';
  return 'error';
}

/** The form drawn from a bootstrap answer, its kept answers filled in. */
export function formOfBootstrap(data: BootstrapResponse): FormDefinition {
  return {
    formId: data.formId,
    formType: data.formType,
    title: data.title,
    description: data.description,
    fields: data.fields,
    ...(data.draft ? { savedData: data.draft.answers } : {}),
  };
}

// ============================================
// The form (Frames 1–2)
// ============================================

export interface FormRequestFormProps {
  form: FormDefinition;
  isAnonymous: boolean;
  submitting: boolean;
  serverErrors: FieldError[];
  /** The last submit failed and the form stays: the line above Submit says so. */
  submitFailed?: boolean;
  /** The pinned Back control, when the entry has somewhere to go back to. */
  back?: ReactNode;
  scrollRef?: Ref<HTMLDivElement>;
  onSubmit: (responses: FormResponseInput[]) => void;
  /** Every change, the whole answer set (web-account-pages D6 save as you go). */
  onAnswersChange: (answers: Record<string, unknown>) => void;
}

/** The sent form's page: the flat header, the request intro, the fields and Submit. */
export function FormRequestForm({
  form,
  isAnonymous,
  submitting,
  serverErrors,
  submitFailed,
  back,
  scrollRef,
  onSubmit,
  onAnswersChange,
}: FormRequestFormProps) {
  return (
    <div className={styles.flow}>
      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.head}>
          {back}
          <div className={styles.headText}>
            <div className={styles.kicker}>Form request</div>
            <h1 className={styles.fname}>{form.title}</h1>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.intro}>
            <div className={styles.req}>{requestTypeLine(form.formType)}</div>
            {form.description && <div className={styles.fdesc}>{form.description}</div>}
            {isAnonymous && (
              <div className={styles.anonPill}>
                <EyeOffIcon />
                Anonymous · your answers aren&rsquo;t linked to your name
              </div>
            )}
          </div>

          <FormRenderer
            form={form}
            onSubmit={onSubmit}
            onSave={noSave}
            onAnswersChange={onAnswersChange}
            isSubmitting={submitting}
            serverErrors={serverErrors}
            disableAutoSave
            appearance="clientBooking"
            renderActions={({ isSubmitting }) => (
              <div className={styles.formbar}>
                {submitFailed && !isSubmitting && (
                  <p className={styles.submitError} role="alert">Couldn't send your answers. Please try again.</p>
                )}
                <button
                  type="submit"
                  className={`${styles.btnPrimary} ${isSubmitting ? styles.busy : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className={styles.btnSpin} aria-hidden="true" />
                      Submitting…
                    </>
                  ) : (
                    <>
                      Submit
                      <ArrowRightIcon />
                    </>
                  )}
                </button>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}

/** A sent form saves through `onAnswersChange`, never FormRenderer's blur and interval save. */
function noSave() {}

/** The loading screen, in the terminal screens' frame. */
export function FormRequestLoading() {
  return (
    <div className={styles.loading}>
      <div className={styles.spinner} />
      <p>Loading form…</p>
    </div>
  );
}

// ============================================
// Terminal screen
// ============================================

export interface TerminalScreenProps {
  state: 'success' | 'consumed' | 'revoked' | 'unavailable' | 'error';
  formName?: string;
  onHome: () => void;
  onRetry: () => void;
}

export function TerminalScreen({ state, formName, onHome, onRetry }: TerminalScreenProps) {
  const content = terminalContent(state, formName);

  return (
    <div className={styles.term}>
      <div className={styles.termCard}>
        <div className={styles.termOrb}>{content.orb}</div>
        {content.kicker && <div className={styles.termKicker}>{content.kicker}</div>}
        <h2>{content.heading}</h2>
        <p>{content.body}</p>
        <div className={styles.termActions}>
          {state === 'error' && (
            <button type="button" className={styles.termBtn} onClick={onRetry}>
              Try again
            </button>
          )}
          <button
            type="button"
            className={`${styles.termBtn} ${state === 'error' ? styles.termBtnSecondary : ''}`}
            onClick={onHome}
          >
            Back to Home
            <ArrowRightIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

export interface TerminalContent {
  orb: ReactNode;
  kicker?: string;
  heading: string;
  body: string;
}

export function terminalContent(
  state: 'success' | 'consumed' | 'revoked' | 'unavailable' | 'error',
  formName?: string,
): TerminalContent {
  switch (state) {
    case 'success':
      return {
        orb: <CircleCheckIcon />,
        kicker: formName,
        heading: 'All set',
        body: 'Your answers are in. The studio has everything it needs.',
      };
    case 'consumed':
      return {
        orb: <CheckIcon />,
        heading: 'Already submitted',
        body: 'This form has been completed. Nothing else is needed from you.',
      };
    case 'revoked':
      return {
        orb: <MinusCircleIcon />,
        heading: 'This form is no longer available',
        body: "This form request has expired. If it's still needed, the studio will send a new one.",
      };
    case 'unavailable':
      return {
        orb: <MinusCircleIcon />,
        heading: 'This form is no longer available',
        body: "The studio may have retired it. If you think that's a mistake, send them a message.",
      };
    case 'error':
    default:
      return {
        orb: <AlertIcon />,
        heading: 'Something went wrong',
        body: 'We couldn’t load this form. Please try again in a moment.',
      };
  }
}

// ============================================
// Icons
// ============================================

export function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

export function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export function CircleCheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

export function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function MinusCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

export function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
